import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { defaultCheck } from "../../../actions/Checks/constants";
import { defaultLayer } from "../../../actions/Layers/constants";
import useConfig from "../../../actions/Tenants/config/configHook";
import {
    CONFIG_TYPES,
    path_advance_ripening,
    path_atron, path_atron_results, path_brix_input, path_color_check,
    path_dry_matter, path_fta_input, path_internal_color_check, path_mangos, path_manual_check,
    path_manual_fruit_form,
    path_manual_or_with_mini,
    path_maturity,
    path_results_with_form,
    path_secondary_manual_check,
    path_secondary_visual_check,
    path_send_check_status_to_tenant,
    path_summary, path_temperature,
    path_visual_check,
    path_weight_input
} from "../../../actions/Tenants/config/constants";
import { useTriggerCheckBusinessRulesMutation } from "../../../hooks/mutations";
import { useCurrentCheck, useCurrentLayer, useRouteCheckQuery, useRouteLayerQuery } from "../../../hooks/useCurrentEntities";
import { RootState } from "../../../reducers";
import { useRegisterDebugTargets } from "../../../utils/useDebugTargets";
import { QcEntityProvider } from "./QcEntityContext";

import { useConnectivity } from "../../../offline/connectivityMonitor";
import { isOfflineChecksEnabled } from "../../../offline/db";
import { callAvosApi } from "../../../utils/useAvosApiHook";
import { Loading } from "../../Helper/Loading";
import NoMatch from "../../NoMatch";
import AdvanceRipening from "./components/AdvanceRipening";
import BrixInput from "./components/BrixInput";
import BulkFTAInput from "./components/BulkFTAInput";
import BulkWeightInput from "./components/BulkWeightInput";
import DeviceResults from "./components/DeviceResults";
import DryMatterInput from "./components/DryMatterInput";
import ManualFruitForm from "./components/ManualFruitForm";
import MaturityInput from "./components/MaturityInput";
import { ExternalColorCheck, InternalColorCheck } from "./components/QualityCheck";
import SendCheckStatusToTenant from "./components/SendExternalNotification/SendCheckStatusToTenantStep";
import CheckSummary from "./components/Summary/CheckSummary";
import TemperatureInput from "./components/TemperatureInput";
import VisualCheck from "./components/VisualCheck";

export default function Check() {
    const params = useParams();
    const tenantId = params.tenant_id as string;
    const param_check_id = useMemo(() => parseFloat(params.check_id as string), [params.check_id]);

    const user = useSelector((state: RootState) => state.auth.user);
    const checkLoading = useRouteCheckQuery().isLoading;
    const layerLoading = useRouteLayerQuery().isLoading;

    const [fetchLayer, setFetchLayer] = useState(false);

    const currentCheckQuery = useCurrentCheck(
        tenantId,
        Number.isNaN(param_check_id) ? undefined : param_check_id,
    );
    const currentLayerQuery = useCurrentLayer(
        tenantId,
        params.layer_id as string,
        fetchLayer,
    );
    const check = currentCheckQuery.data ?? defaultCheck;
    const layer = currentLayerQuery.data ?? defaultLayer;

    const config = useConfig();
    const tenant_id = (user as any)?.tenant?.tenant_id;

    // * Register layer_type shortcut (direct link to config)
    const layer_type_config = layer?.type ? config.configs.find((c) => c.value === layer.type && c.type === CONFIG_TYPES.LAYER_TYPE) : null;
    useRegisterDebugTargets("layer_type", tenant_id && layer_type_config ? [{
        id: `layer_type_${layer_type_config.id}`,
        url: `/tenants/${tenant_id}/config/${layer_type_config.id}`,
        text: `${layer_type_config.config.text} config`,
    }] : []);

    // * Register layer data inspection modal
    useRegisterDebugTargets("layer_data", tenant_id && layer ? [{
        id: `layer_data_${layer.id}`,
        url: "#",
        text: "Layer data",
        debug: {
            props: { data: layer },
            title: `Debug ${layer_type_config?.config.text || "Layer"} ${layer.label}`,
        },
    }] : []);

    // * Register check_location shortcut (direct link to config)
    const check_location_config = check?.location ? config.configs.find((c) => c.value === check.location && c.type === CONFIG_TYPES.CHECK_LOCATION) : null;
    useRegisterDebugTargets("check_location", tenant_id && check_location_config ? [{
        id: `check_location_${check_location_config.id}`,
        url: `/tenants/${tenant_id}/config/${check_location_config.id}`,
        text: `${check_location_config.config.text} config`,
    }] : []);

    // * Register check data inspection modal
    useRegisterDebugTargets("check_data", tenant_id && check ? [{
        id: `check_data_${check.check_id}`,
        url: "#",
        text: "Check data",
        debug: {
            props: { data: check },
            title: `Debug check ${check.check_id}`,
        },
    }] : []);


    const triggerBusinessRulesMutation = useTriggerCheckBusinessRulesMutation();
    const { isOnline } = useConnectivity();
    const inOfflineMode = isOfflineChecksEnabled(config) && !isOnline;

    useEffect(() => {
        return () => {
            // Skip the business-rules trigger when offline: the synthetic
            // check_id has no server row and the call would 404 / fail
            // with ERR_INTERNET_DISCONNECTED, surfacing as an uncaught
            // promise rejection in CRA's dev overlay. Business rules are
            // re-applied at replay time anyway. Also defensively catch
            // any rejection so a transient online failure here doesn't
            // explode the unmount path.
            if (inOfflineMode) return;
            if (param_check_id && (check?.check_id === param_check_id)) {
                callAvosApi(`/checks/${check?.check_id}/business-rules`).catch(() => {
                    // Best-effort: business-rules will run again on the
                    // next mount or via replay; nothing to surface here.
                });
            }
        };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (param_check_id > 0) {
                // Offline path: the synthetic check only exists in the query
                // cache, so don't refetch — the synthetic check_id has no
                // server row. Business rules are re-applied at replay time.
                if (inOfflineMode) {
                    setFetchLayer(true);
                    return;
                }

                // * call business rules every time we go to summary page
                if (params.action === path_summary) {
                    await triggerBusinessRulesMutation
                        .mutateAsync({ check: { tenant_id: tenantId, check_id: param_check_id } })
                        .catch(() => undefined);
                }
            }

            setFetchLayer(true); // set fetch layer after business rules are fetched
        };

        fetchData();
    }, [params.check_id, params.action, inOfflineMode, tenantId]);

    const disable_loading = [path_advance_ripening];

    if (
        !check.check_id
        || !layer.id
        || checkLoading
        || layerLoading
        || currentCheckQuery.isLoading
        || currentLayerQuery.isLoading
        || String(layer.id) !== params.layer_id
        || parseFloat(params.check_id as string) !== check.check_id
    ) {
        if (!disable_loading.includes(params.action as string)) {
            return <Loading />;
        }
    }

    const location_config = config.get_check_type_config(layer, check);


    if (!location_config) {
        return <Loading />;
    }

    const content = (() => {
        switch (params.action) {
        case path_atron:
        case path_atron_results:
        case path_mangos:
        case path_results_with_form:
            return <DeviceResults />;

        case path_manual_or_with_mini: {
            if (check.is_manual) {
                return <ManualFruitForm />;
            }

            return <DeviceResults />;
        }

        case path_manual_check:
        case path_manual_fruit_form:
            return <ManualFruitForm />;

        case path_secondary_manual_check:
            return <ManualFruitForm type="secondary"/>;

            // case path_external_quality_check:
            //     return <ExternalQualityCheck />;

        case path_fta_input:
            return <BulkFTAInput />;

        case path_weight_input:
            return <BulkWeightInput />;

        case path_brix_input:
            return <BrixInput />;

        case path_dry_matter:
            return <DryMatterInput />;

        case path_internal_color_check:
            return <InternalColorCheck />;

            // case path_internal_quality_check:
            //     return <InternalQualityCheck />;

        case path_color_check:
            return <ExternalColorCheck />;

        case path_temperature:
            return <TemperatureInput />;

        case path_maturity:
            return <MaturityInput />;

        case path_summary:
            return <CheckSummary />;

        case path_send_check_status_to_tenant:
            return <SendCheckStatusToTenant />;

        case path_visual_check:
        case path_secondary_visual_check:
            return <VisualCheck />;

        case path_advance_ripening:
            return <AdvanceRipening setFetchLayer={setFetchLayer} />;

        default:
            return <NoMatch />;
        }
    })();

    return <QcEntityProvider layer={layer} check={check}>{content}</QcEntityProvider>;
}
